import React, { useState } from "react";
import {
  Button, Dropdown, Input, Popconfirm, Space,
} from "antd";
import {
  CheckOutlined, DeleteOutlined, EditOutlined, MoreOutlined,
  PushpinFilled, PushpinOutlined, SaveOutlined, CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Comment, CommentAuthor } from "@shared/types/employee";
import type { CommentTag } from "@shared/types";
import {
  FONT_SIZE,
  SPACE,
  TONE,
} from "@ds/tokens/palette";
import type { ToneName } from "@ds/tokens/palette";
import { TextValue } from "@ds/display/TextValue";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Pill } from "@ds/display/Pill";
import { Icon } from "@ds/display/Icon";
import { IconButton } from "@ds/primitives/IconButton";
import { useFormat } from "@ds/display/useNumberFormat";
import { useCommentActions } from "./useCommentActions";
import { CommentEditor } from "./CommentEditor";
import { CommentAvatar } from "./CommentAvatar";

const { TextArea } = Input;

export type CommentCardMode = "full" | "compact" | "inline";

interface CommentCardProps {
  comment: Comment;
  mode: CommentCardMode;
  onClick?: () => void;
  onMutated?: () => void;
}

// ─── tag config ──────────────────────────────────────────────────────────────

type TagCfg = { labelKey: string; tone: ToneName };

const TAG_CFG: Record<CommentTag, TagCfg> = {
  cancellation_reason:    { labelKey: "comments.tag.cancellation_reason",    tone: "red"   },
  price_change:           { labelKey: "comments.tag.price_change",           tone: "amber" },
  system_note:            { labelKey: "comments.tag.system_note",            tone: "grey"  },
  transport_instructions: { labelKey: "comments.tag.transport_instructions", tone: "teal"  },
};

function TagBadge({ tag }: { tag: CommentTag }) {
  const { t } = useTranslation();
  const cfg = TAG_CFG[tag];
  return (
    <Pill tone={cfg.tone} variant="outline" size="sm">
      {t(cfg.labelKey)}
    </Pill>
  );
}

// ─── CommentCard ──────────────────────────────────────────────────────────────

export const CommentCard: React.FC<CommentCardProps> = ({
  comment, mode, onClick, onMutated,
}) => {
  const { t } = useTranslation();
  const { relativeTime } = useFormat();
  const actions = useCommentActions();
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editTag, setEditTag] = useState<CommentTag | null>(null);

  const author = (comment.created_by_employee as CommentAuthor | null) ?? null;
  const resolver = (comment.resolved_by_employee as CommentAuthor | null) ?? null;
  const authorName = author?.full_name ?? t("comments.author.system");
  const initials = authorName[0]?.toUpperCase() ?? "?";
  const isResolved = !!comment.resolved_at;
  const isPinned = comment.is_pinned && !isResolved;

  const startEdit = () => {
    setEditContent(comment.content);
    setEditTag(comment.tag);
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = () => {
    if (!editContent.trim()) return;
    actions.update(comment.id, {
      content: editContent,
      tag: editTag as never,
      onSuccess: () => { setEditing(false); onMutated?.(); },
    });
  };

  const handleResolve = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    actions.resolve(comment.id, { onSuccess: onMutated });
  };

  const handlePin = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    actions.pin(comment.id, { onSuccess: onMutated });
  };

  const handleUnpin = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    actions.unpin(comment.id, { onSuccess: onMutated });
  };

  const handleDelete = () => {
    actions.remove(comment.id, { onSuccess: onMutated });
  };

  // ── Inline mode (read-only, no card) ───────────────────────────────
  if (mode === "inline") {
    return (
      <TextValue as="span" size="sm" tone="secondary">
        {isPinned && <PushpinFilled style={{ color: TONE.amber.base, fontSize: FONT_SIZE.label, marginRight: SPACE.xs }} />}
        {initials} · {relativeTime(comment.created_at)} — {comment.content}
      </TextValue>
    );
  }

  // ── Compact mode (sub-row — subtle secondary line) ──────────────────
  if (mode === "compact") {
    if (editing) {
      return (
        <Box paddingX="md" paddingY="xs" onClick={(e) => e.stopPropagation()}>
          <TextArea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            autoFocus
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={2000}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
              if (e.key === "Escape") cancelEdit();
            }}
            style={{ marginBottom: SPACE.xs, fontSize: FONT_SIZE.small }}
          />
          <Space size={4}>
            <Button type="primary" size="small" icon={<SaveOutlined />} onClick={saveEdit} disabled={!editContent.trim()}>
              {t("common.actions.save")}
            </Button>
            <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>
              {t("common.actions.cancel")}
            </Button>
          </Space>
        </Box>
      );
    }

    // Outer wrapper carries keyboard + double-click affordances. No `style`
    // (cursor:pointer drift accepted — interactive role still announced).
    return (
      <div
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onDoubleClick={(e: React.MouseEvent) => { e.stopPropagation(); startEdit(); }}
        onKeyDown={onClick ? (e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      >
        <Box paddingX="md" paddingY="xs">
          <Inline gap="xs" align="center">
            {isPinned && (
              <PushpinFilled style={{ color: TONE.amber.base, fontSize: FONT_SIZE.label, flexShrink: 0 }} aria-hidden />
            )}
            <TextValue as="span" size="sm" tone="secondary" truncate>
              {comment.content}
            </TextValue>
            <Box as="span" grow />
            <TextValue as="span" size="xs" tone="faint" nowrap>
              {initials} · {relativeTime(comment.created_at)}
            </TextValue>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "resolve", icon: <CheckOutlined />, label: t("comments.actions.resolve"), onClick: () => handleResolve() },
                  { key: "edit", icon: <EditOutlined />, label: t("common.actions.edit"), onClick: startEdit },
                  isPinned
                    ? { key: "unpin", icon: <PushpinOutlined />, label: t("comments.actions.unpin"), onClick: () => handleUnpin() }
                    : { key: "pin", icon: <PushpinFilled />, label: t("comments.actions.pin"), onClick: () => handlePin() },
                  { type: "divider" as const },
                  { key: "delete", icon: <DeleteOutlined />, label: t("common.actions.delete"), danger: true, onClick: handleDelete },
                ],
              }}
            >
              <Button type="text" size="small" icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()} aria-label="more" />
            </Dropdown>
          </Inline>
        </Box>
      </div>
    );
  }

  // ── Full mode (drawer) ─────────────────────────────────────────────
  // Edit-in-place takes over the whole row when active.
  if (editing) {
    return (
      <Box paddingY="sm" divider="bottom" position="relative">
        <Inline gap="sm" align="start">
          <CommentAvatar photo={author?.thumbnail_photo_url} initials={initials} />
          <Box grow>
            <CommentEditor
              content={editContent}
              onChange={setEditContent}
              tag={editTag}
              onTagChange={setEditTag}
              onSubmit={saveEdit}
              onCancel={cancelEdit}
              submitLabel={t("common.actions.save")}
              autoFocus
              alwaysShowFooter
            />
          </Box>
        </Inline>
      </Box>
    );
  }

  return (
    <Box paddingY="sm" divider="bottom" position="relative" testId={`comment-${comment.id}`}>
      <Inline gap="sm" align="start">
        <CommentAvatar photo={author?.thumbnail_photo_url} initials={initials} />

        <Box grow>
          <Stack gap="xs">
            {/* Header row */}
            <Inline gap="xs" align="center">
              <TextValue as="span" size="sm" weight="semibold" tone="primary">
                {authorName}
              </TextValue>
              <TextValue as="span" size="xs" tone="faint">·</TextValue>
              <TextValue as="span" size="sm" tone="faint" nowrap>
                {relativeTime(comment.created_at)}
                {comment.updated_at && comment.updated_at !== comment.created_at && (
                  <> ({t("comments.fields.edited")})</>
                )}
              </TextValue>
              {isPinned && <Icon name="pin" size="sm" tone="amber" />}

              <Box as="span" grow />

              {/* Action buttons */}
              <Inline gap="none" align="center">
                <IconButton
                  variant="ghost"
                  size="sm"
                  tone={isPinned ? "blue" : "neutral"}
                  ariaLabel={isPinned ? t("comments.actions.unpin") : t("comments.actions.pin")}
                  icon={<Icon name="pin" size="sm" />}
                  onClick={isPinned ? handleUnpin : handlePin}
                />

                {!isResolved && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    ariaLabel={t("comments.actions.resolve")}
                    icon={<Icon name="check" size="sm" />}
                    onClick={handleResolve}
                    testId={`comment-resolve-${comment.id}`}
                  />
                )}

                <IconButton
                  variant="ghost"
                  size="sm"
                  ariaLabel={t("common.actions.edit")}
                  icon={<Icon name="editPen" size="sm" />}
                  onClick={startEdit}
                  testId={`comment-edit-${comment.id}`}
                />

                <Popconfirm
                  title={t("comments.messages.delete_confirm")}
                  onConfirm={handleDelete}
                  okText={t("common.actions.confirm")}
                  cancelText={t("common.actions.cancel")}
                >
                  <IconButton
                    variant="ghost"
                    size="sm"
                    tone="red"
                    ariaLabel={t("common.actions.delete")}
                    icon={<Icon name="delete" size="sm" />}
                    onClick={() => { /* Popconfirm intercepts */ }}
                  />
                </Popconfirm>
              </Inline>
            </Inline>

            {/* Tag */}
            {comment.tag && <TagBadge tag={comment.tag} />}

            {/* Content — split on newline to preserve multi-line authored text
                without a raw `whiteSpace: pre-wrap` style. */}
            <Stack gap="none">
              {comment.content.split("\n").map((line, i) => (
                <TextValue
                  key={i}
                  as="div"
                  size="sm"
                  lineHeight="normal"
                  tone={isResolved ? "muted" : "primary"}
                  wrapWords
                >
                  {line || " "}
                </TextValue>
              ))}
            </Stack>

            {/* Resolved footer */}
            {isResolved && (
              <Inline gap="xs" align="center">
                <Icon name="check" size="sm" tone="green" />
                <TextValue as="span" size="xs" tone="faint">
                  {t("comments.resolved")}
                  {resolver?.full_name && <> · {resolver.full_name}</>}
                  {comment.resolved_at && <> · {relativeTime(comment.resolved_at)}</>}
                </TextValue>
              </Inline>
            )}
          </Stack>
        </Box>
      </Inline>
    </Box>
  );
};
